import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { discountsApi, InventoryModel } from '@pukka/api';
import { toast } from '@pukka/toast';
import { Alert, Button, ScrollArea } from '@pukka/ui-components';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FaTrashAlt } from 'react-icons/fa';

import DiscountForm from './DiscountForm';
import { DiscountCreateFormSchema, DiscountCreateFormSchemaType } from './discountFormSchema';
import discountState from './discountStore';
import { getAmountFromData, getOutletIdsFromDiscount, getTypeFromData } from './utils';

interface DiscountEditBoxProps {
  readonly discount: InventoryModel.Discount.List200ResponseInner;
  readonly onClose: () => void;
}

function DiscountEditBox({ onClose, discount }: DiscountEditBoxProps) {
  const { data: discountDetail } = discountsApi.useDiscountDetail({
    name: discount.name,
    type: discount.type,
    amount: discount.amount ? Number(discount.amount) : undefined,
  });

  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

  const methods = useForm<DiscountCreateFormSchemaType>({
    resetOptions: {
      keepDirtyValues: true,
    },
    defaultValues: {
      name: discount.name,
      outlet_ids: discount.outlets.map((outlet) => outlet.outlet_id).sort(),
      category: discount.amount ? 'fixedAmount' : 'customAmount',
      amountType: discount.type,
      discountTypeCustom: discount.type,
      amount: discount.amount ?? undefined,
    },
    resolver: zodResolver(DiscountCreateFormSchema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods;

  useEffect(() => {
    reset({
      name: discount?.name,
      category: discount.amount ? 'fixedAmount' : 'customAmount',
      amountType: discount.type,
      outlet_ids: discountDetail?.outlet_ids.sort() || [],
      discountTypeCustom: discount.type,
      amount: discount.amount ?? undefined,
    });
  }, [discount, discountDetail, reset]);

  const { mutate: updateDiscount } = useMutation(discountsApi.editDiscount, {
    onSuccess: () => {
      discountState.dirtyFormExists = false;
      onClose();
      toast.success('Discount has been updated');
      discountsApi.invalidateDiscounts();
    },
    onError: ({ message }: AxiosError) => {
      toast.error(message);
    },
  });

  const { mutate: deleteDiscount } = useMutation(discountsApi.deleteDiscount, {
    onSuccess: () => {
      discountState.dirtyFormExists = false;
      reset();
      onClose();
      toast.success('Discount has been deleted');
      discountsApi.invalidateDiscounts();
    },
    onError: async ({ message }: AxiosError) => {
      toast.error(message);
    },
  });

  const onSubmit = handleSubmit((data) => {
    const originalOutlets = getOutletIdsFromDiscount(discountDetail, discount);
    const deletedOutletIds: number[] = [];

    originalOutlets.forEach((outlet) => {
      const existsInUpdatedOutletIds = !!data.outlet_ids.find((outletId) => Number(outlet) === Number(outletId));

      if (!existsInUpdatedOutletIds) {
        deletedOutletIds.push(outlet);
      }
    });

    const body = {
      original_name: discount.name,
      original_amount: discount.amount,
      original_type: discount.type,
      amount: getAmountFromData(data),
      name: data.name,
      updated_outlet_ids: data.outlet_ids,
      deleted_outlet_ids: deletedOutletIds,
      type: getTypeFromData(data),
    };

    updateDiscount(body);
  });

  const onDeleteConfirm = () => {
    deleteDiscount({
      amount: discount.amount,
      name: discount.name,
      type: discount.type,
    });
  };

  return (
    <>
      <form name="discount-edit" className="relative h-full overflow-auto">
        <ScrollArea className="flex min-h-full flex-col overflow-auto bg-neutral-100 p-5 pb-32 md:pb-20">
          <div className="h-full rounded border border-neutral-200 bg-white">
            <div className="border-b border-neutral-200 px-3 py-2.5 text-sm font-medium text-neutral-500">DISCOUNT INFORMATION</div>
            <FormProvider {...methods}>
              <DiscountForm />
            </FormProvider>
          </div>
        </ScrollArea>

        <Alert
          type="danger"
          title={`Delete ${discount.name}`}
          okButtonName="Delete"
          description={`This action cannot be undone. Discount will be deleted from ${discount.total_outlets} outlets. Are you sure?`}
          open={deleteAlertOpen}
          onOpenChange={setDeleteAlertOpen}
          onConfirm={onDeleteConfirm}
        />
      </form>
      <div className="absolute bottom-0 flex w-full items-center justify-end gap-x-2 bg-neutral-200 px-4 py-2.5">
        <Button aria-label="delete button" variant="danger" className="mr-auto" onClick={() => setDeleteAlertOpen(true)}>
          <FaTrashAlt size={14} />
        </Button>
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!isDirty} onClick={onSubmit}>
          Save
        </Button>
      </div>
    </>
  );
}

export default DiscountEditBox;
